/**
 * Zod wire schemas for Gong Engage Flows endpoints consumed by `GongClient`
 * (`src/services/gong/gong-client.ts`) — `listFlows`, `getFlowSteps`,
 * `assignProspects`, `unassignByCrmId`, plus the pre-existing
 * `getFlowProspects` response.
 *
 * Field names/shapes are camelCase per Gong's actual JSON wire format,
 * cross-checked against the OpenAPI-generated `gong-rs` client
 * (https://github.com/cedricziel/gong-rs/tree/main/docs — `FlowsResponse`,
 * `Records`, `PublicApiFlow`, `AssignFlowRequestV2`, `AssignFlowResponse`,
 * `PublicApiAssignedFlow`, `AssignedFlowFailure`) and against the live-call
 * facts recorded in `docs/flow/gong-flow-play-spec-handoff.md` (Checks 2-4,
 * confirmed 2026-08-18/19 against production Gong). The `getFlowSteps`
 * request/response envelope (`{ flowIds }` in, `{ flows: [{id, steps}] }`
 * out) follows the SAME `flows`-array convention as `listFlows`
 * (`FlowsResponse`) but was not independently captured raw — if a live call
 * shows a different envelope key, only this one schema needs to change.
 *
 * `GongFlowProspectSchema`/`GongProspectsResponseSchema` are the home for the
 * `POST /v2/flows/prospects` (`getFlowProspects`) contract, moved here from
 * `gong-client.ts` per U7 (`gong-flow-play-and-queue-redesign` plan, OQ1
 * resolution). mini-apps cannot import `src/**` (`no-restricted-imports`,
 * `mini-apps/CLAUDE.md`), so this is the only copy of the schema.
 *
 * The call endpoints (`GET /v2/calls`, `POST /v2/calls/extensive`,
 * `POST /v2/calls/transcript`) live at the bottom of this file. They are the
 * contract for what the client reads; REAL scrubbed responses in
 * `src/services/gong/__tests__/fixtures/recorded/` keep the full shape, and
 * `gong-recorded-fixtures.test.ts` parses them through these schemas.
 */

import { z } from 'zod';
import { GongFlowStepActionTypeSchema } from './gong-flow';
import { logger } from '../../utils/logger';

// GET /v2/flows/prospects — flow-instance lookup (moved from gong-client.ts)

/** A single entry in Gong's `POST /v2/flows/prospects` `prospectsAssigned` array. */
export const GongFlowProspectSchema = z.object({
  flowId: z.string(),
  flowName: z.string(),
  crmProspectId: z.string(),
  flowInstanceId: z.string(),
  flowInstanceOwnerEmail: z.string(),
  flowInstanceOwnerFullName: z.string(),
  flowInstanceCreateDate: z.string().nullable().optional(),
  flowInstanceStatus: z.string(),
});

export type GongFlowProspect = z.infer<typeof GongFlowProspectSchema>;

/**
 * Gong `POST /v2/flows/prospects` response. `prospectsAssigned` is a
 * deliberately REQUIRED key: Gong returns `prospectsAssigned: []` (key
 * present) when a contact has no active flows, so a missing key means a
 * malformed/unexpected response shape and must be a parse failure — not an
 * empty result that would silently mark every checked contact stale.
 */
export const GongProspectsResponseSchema = z.object({
  prospectsAssigned: z.array(GongFlowProspectSchema),
});

/**
 * Shared Gong batch width for `POST /v2/flows/prospects` (Gong's documented
 * per-call id limit). Lives here, not in a service module, so the request
 * schema below and the reconcilers share one literal without `src/shared`
 * importing from `src/services`.
 */
export const GONG_FLOW_PROSPECTS_BATCH_SIZE = 200;

/** Request body for `POST /v2/flows/prospects` when querying by flow instance id. */
export const GongProspectsByInstanceRequestSchema = z.object({
  flowInstanceIds: z.array(z.string()).min(1).max(GONG_FLOW_PROSPECTS_BATCH_SIZE),
});

export type GongProspectsByInstanceRequest = z.infer<typeof GongProspectsByInstanceRequestSchema>;

// GET /v2/flows — listFlows (cursor pagination)

/**
 * One row of `GET /v2/flows`. Confirmed live 2026-08-18 (spec handoff Check
 * 2): exactly these fields — NO `status`, NO `modifiedDate`, NO
 * creator/owner field.
 */
export const GongFlowListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  folderId: z.string(),
  folderName: z.string(),
  visibility: z.string(),
  creationDate: z.string(),
  exclusive: z.boolean(),
});

export type GongFlowListItem = z.infer<typeof GongFlowListItemSchema>;

/** Gong's shared list-endpoint pagination envelope (`Records` in the OpenAPI spec). */
const GongRecordsSchema = z.object({
  totalRecords: z.number().optional(),
  currentPageSize: z.number().optional(),
  currentPageNumber: z.number().optional(),
  cursor: z.string().optional(),
});

export const GongFlowsListResponseSchema = z.object({
  records: GongRecordsSchema.optional(),
  flows: z.array(GongFlowListItemSchema),
});

// POST /v2/flows/steps — getFlowSteps (≤20 flow ids per call)

/**
 * Known-values set for `GongFlowStepActionTypeSchema`, reused below by the
 * wire-tolerant coercion.
 */
const KNOWN_ACTION_TYPES = new Set<string>(GongFlowStepActionTypeSchema.options);

/**
 * Wire-tolerant variant of `GongFlowStepActionTypeSchema`, used ONLY at the
 * `GongFlowStepDetailSchema.action` field below (the live `POST
 * /v2/flows/steps` read boundary) — every other consumer of the closed
 * 6-value enum (`mapStepsForStorage` in
 * `src/services/gong-flows-sync/seams.ts`, the persisted-mirror read schema
 * `GongFlowStepSchema.actionType` in `gong-flow.ts`, and the browse wire
 * contract `GongFlowStepDetailWireSchema.step_type` in
 * `src/shared/flow/gong-flow-catalog.ts`) stays on the closed enum
 * untouched, because by the time data reaches them it is already one of the
 * 6 known values.
 *
 * Why this exists: Gong can add a new Engage flow step action type at any
 * time without notice, and Terra does not control that surface. A closed
 * `z.enum` sitting directly on a live external-read boundary is an
 * operational landmine — one unrecognized value anywhere in Gong's ~660-flow
 * catalog would throw a `ZodError` out of `getFlowSteps` and stall every
 * caller populating the `gong_flows` mirror (`browseGongFlows`, the on-demand
 * steps endpoint) until a code change widened the enum. This
 * is a narrower analogue of the repo's existing read-side-string /
 * write-side-enum convention (`EnrollmentItemStep.copySource`,
 * `GongFlowUserPref.state`, `preferred_channel` —
 * `prisma/schema/core.prisma:1331/:1388-1393/:1443-1445`): the enum stays
 * closed everywhere except this one true external-read boundary, which
 * becomes tolerant instead of throwing.
 *
 * `SEND_EMAIL` and `AUTO_SEND_EMAIL` — the automation-flow deny-list
 * distinction (`gong-flow.ts` header) — are never coercion targets; both are
 * known values and pass through unchanged.
 *
 * Known loss: a non-empty string outside the 6 known options — e.g. a
 * legitimate future 7th Gong type — coerces to `'CUSTOM'`. The mirror never
 * records the true unknown type string; it renders as `CUSTOM` until the
 * enum is deliberately widened. Accepted as a deliberate lossy-but-minimal
 * trade-off (see plan `2026-08-19-002-gong-flow-play-blockship-fixes-plan.md`,
 * Decision #6).
 *
 * The boundary tolerates unknown type NAMES, not malformed VALUES: a
 * missing (`z.preprocess` runs on absent keys, receiving `undefined`),
 * `null`, non-string, or empty-string `action` passes through the preprocess
 * unchanged and fails the wrapped enum with a typed Zod error — rejecting
 * that chunk's parse so the mirror keeps its last-known-good rows, exactly
 * like every sibling field (`subject: null` is a parse failure). Coercing
 * those would let a transient malformed Gong response silently downgrade a
 * mirrored email step to `CUSTOM` and hide its subject from reps (round-3
 * finding B1, plan `2026-08-19-003-gong-flow-flush-race-and-coercion-fixes-plan.md`,
 * which overturned the prior "non-string values also coerce" acceptance).
 */
const GongFlowStepActionTypeWireSchema = z.preprocess((val) => {
  if (typeof val !== 'string' || val.length === 0 || KNOWN_ACTION_TYPES.has(val)) return val;
  logger.warn({ val }, '[Gong] Unknown flow step action type coerced to CUSTOM');
  return 'CUSTOM';
}, GongFlowStepActionTypeSchema);

/**
 * One step of a flow, as returned by `POST /v2/flows/steps`. Confirmed live
 * 2026-08-18 (spec handoff Check 3): every step type (not just
 * `SEND_EMAIL`) carries `subject` + `body`, but `subject` is only
 * REQUIRED-present on email-shaped steps in practice — the schema treats it
 * as optional (missing is valid; `null` is a parse failure, same as every
 * other field) so a non-email step that omits it still parses.
 */
export const GongFlowStepDetailSchema = z.object({
  stepOrder: z.number().int().positive(),
  action: GongFlowStepActionTypeWireSchema,
  subject: z.string().optional(),
  body: z.string(),
  isReply: z.boolean(),
  daysFromPreviousStep: z.number(),
});

export type GongFlowStepDetail = z.infer<typeof GongFlowStepDetailSchema>;

/** One requested flow's steps. `steps: []` is valid (e.g. a flow with no steps). */
export const GongFlowStepsEntrySchema = z.object({
  id: z.string(),
  steps: z.array(GongFlowStepDetailSchema),
});

export type GongFlowStepsEntry = z.infer<typeof GongFlowStepsEntrySchema>;

export const GongFlowStepsResponseSchema = z.object({
  flows: z.array(GongFlowStepsEntrySchema),
});

// POST /v2/flows/prospects/assign — assignProspects

/**
 * One override in `overrides.steps[]`. `number` is 1-based, matching
 * `GongFlowStepDetail.stepOrder` (spec handoff: "`stepOrder` is 1-based and maps
 * to assign's `overrides.steps[].number`"). `subject`/`body` are both
 * optional — a non-email step override may only set `body` (script text).
 */
export const GongAssignStepOverrideSchema = z.object({
  number: z.number().int().positive(),
  subject: z.string().optional(),
  body: z.string().optional(),
});

export type GongAssignStepOverride = z.infer<typeof GongAssignStepOverrideSchema>;

/**
 * `POST /v2/flows/prospects/assign` request. `flowInstanceVariables` is
 * plumbed per U7/plan but always sent as an EMPTY map in v0 (U14 cut) — the
 * field exists so a future producer can populate it with no schema change.
 */
export const GongAssignProspectsRequestSchema = z.object({
  crmProspectsIds: z.array(z.string()),
  flowId: z.string(),
  flowInstanceOwnerEmail: z.string(),
  coolOffOverride: z.boolean().optional(),
  flowInstanceVariables: z.record(z.string(), z.string()).optional(),
  overrides: z.object({ steps: z.array(GongAssignStepOverrideSchema) }).optional(),
});

export type GongAssignProspectsRequest = z.infer<typeof GongAssignProspectsRequestSchema>;

/**
 * One successful assignment. Confirmed live 2026-08-19 (spec handoff Check
 * 4): `prospectsAssigned[0]` = `{flowInstanceId, flowInstanceStatus,
 * flowInstanceOwnerEmail, flowInstanceCreateDate, workspaceId, exclusive}`.
 * `flowInstanceId` is the only field callers may rely on being present
 * (behavior spec) — the rest are carried through as optional extras per the
 * OpenAPI-documented `PublicApiAssignedFlow` shape.
 */
export const GongAssignedFlowSchema = z.object({
  flowId: z.string().optional(),
  flowName: z.string().optional(),
  crmProspectId: z.string().optional(),
  flowInstanceId: z.string(),
  flowInstanceOwnerEmail: z.string().optional(),
  flowInstanceOwnerFullName: z.string().optional(),
  flowInstanceCreateDate: z.string().nullable().optional(),
  flowInstanceStatus: z.string().optional(),
  workspaceId: z.string().optional(),
  exclusive: z.boolean().optional(),
});

export type GongAssignedFlow = z.infer<typeof GongAssignedFlowSchema>;

/**
 * One failed assignment. `errorCode`/`errorMessage` are REQUIRED — a
 * `prospectsNotAssigned` entry without them is a malformed response, not a
 * "silent success". The gong_flow handler renders it for the rep's
 * `last_send_error`: `"errorCode: errorMessage"` only for an allowlisted code,
 * else the code alone (see `gongRejectionMessage` in execution-handlers/gong-flow.ts).
 */
export const GongAssignedFlowFailureSchema = z.object({
  flowId: z.string().optional(),
  crmProspectId: z.string().optional(),
  errorCode: z.string(),
  errorMessage: z.string(),
});

export type GongAssignedFlowFailure = z.infer<typeof GongAssignedFlowFailureSchema>;

export const GongAssignProspectsResponseSchema = z.object({
  prospectsAssigned: z.array(GongAssignedFlowSchema),
  prospectsNotAssigned: z.array(GongAssignedFlowFailureSchema),
});

export type GongAssignProspectsResponse = z.infer<typeof GongAssignProspectsResponseSchema>;

// POST /v2/flows/prospects/unassign-flows-by-crm-id — unassignByCrmId

/**
 * `POST /v2/flows/prospects/unassign-flows-by-crm-id` request. Omitting
 * `flowId` removes the prospect from ALL flows (spec handoff, verified
 * live) — the send path always passes a `flowId` to scope the removal to
 * one flow.
 */
export const GongUnassignRequestSchema = z.object({
  crmProspectId: z.string(),
  flowId: z.string().optional(),
  unassignedByUserEmail: z.string(),
});

export type GongUnassignRequest = z.infer<typeof GongUnassignRequestSchema>;

/**
 * Terra-side guard for Archive (gong reconcile U16, plan D7): the same wire
 * request with `flowId` REQUIRED, so a removal is always scoped to ONE flow.
 * Build every Archive unassign through this schema's `parse` — a missing
 * `flowId` throws here and an all-flows unassign can never reach the client.
 * `GongUnassignRequestSchema` stays the client's (optional-`flowId`) wire shape.
 */
export const GongUnassignOneFlowRequestSchema = GongUnassignRequestSchema.extend({
  flowId: z.string().min(1),
});

export type GongUnassignOneFlowRequest = z.infer<typeof GongUnassignOneFlowRequestSchema>;

export const GongUnassignResponseSchema = z.object({
  unassignedFlowInstanceIds: z.array(z.string()),
});

export type GongUnassignResponse = z.infer<typeof GongUnassignResponseSchema>;

// gong_flow execution-handler input (Terra-internal — NOT a Gong wire
// endpoint)

/**
 * The flat payload `src/services/flow/enrollment-sender.ts`'s `sendGongFlow`
 * builds and passes to `executeUnbound` as `inputs` — the producer contract
 * for `src/services/execution-handlers/gong-flow.ts`'s `executeGongFlow`
 * consumer (Gong Flow Play + Queue redesign, U10 Behavior Specs #1-6).
 * Reuses `GongAssignStepOverrideSchema` (above, same file) for
 * `overrides.steps[]` rather than declaring a second copy — this envelope is
 * a Terra-owned RESHAPE of `GongAssignProspectsRequestSchema` (a singular
 * `crmProspectId` instead of the Gong API's `crmProspectsIds` array — v0
 * sends one prospect per call), so it is never sent to Gong verbatim;
 * `executeGongFlow` builds the real `GongAssignProspectsRequest` from it
 * before calling `assignProspects`. Declared here (not in `gong-flow.ts`,
 * the DB-mirror schema module) purely to reuse `GongAssignStepOverrideSchema`
 * without introducing a `gong.ts` <-> `gong-flow.ts` import cycle (`gong.ts`
 * already imports `GongFlowStepActionTypeSchema` from `gong-flow.ts`).
 */
export const GongFlowSendInputSchema = z.object({
  flowInstanceOwnerEmail: z.string().min(1),
  crmProspectId: z.string().min(1),
  flowId: z.string().min(1),
  overrides: z.object({ steps: z.array(GongAssignStepOverrideSchema) }).optional(),
  coolOffOverride: z.boolean(),
  /** Always `{}` in v0 (U14 cut) — plumbed for a future producer, never populated by `sendGongFlow`. */
  flowInstanceVariables: z.record(z.string(), z.never()),
}).strict();

export type GongFlowSendInput = z.infer<typeof GongFlowSendInputSchema>;

export const GongClientCallSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  started: z.string(),
  duration: z.number(),
  url: z.string().optional(),
  parties: z
    .array(
      z.object({
        name: z.string(),
        emailAddress: z.string().optional(),
        speakerId: z.string().optional(),
      }),
    )
    .optional(),
});
export type GongCallSummary = z.infer<typeof GongClientCallSummarySchema>;

export const GongClientCallWithTranscriptSchema = z.object({
  call: GongClientCallSummarySchema,
  transcript: z.string(),
  speakerMap: z.record(z.string(), z.string()),
});
export type GongCallWithTranscript = z.infer<typeof GongClientCallWithTranscriptSchema>;

// Calls endpoints — the contract for what `GongClient` READS

/**
 * These schemas cover only the fields the client reads. Zod objects drop
 * unknown keys, so a wrong type in a field the client never reads (for
 * example `isPrivate: null`) cannot fail a request; do not add unread
 * fields here. The recorded fixtures in
 * `src/services/gong/__tests__/fixtures/recorded/` keep the full real shape
 * and prove the read fields parse from real responses.
 */

/**
 * One call as returned FLAT by `GET /v2/calls` and nested under `metaData` by
 * `POST /v2/calls/extensive`. `title`, `started`, `duration` and `url` are
 * optional because the client supplies fallbacks for them.
 */
export const GongCallListItemSchema = z.object({
  id: z.string(),
  url: z.string().optional(),
  title: z.string().optional(),
  started: z.string().optional(),
  duration: z.number().optional(),
});

export type GongCallListItem = z.infer<typeof GongCallListItemSchema>;

export const GongCallsListResponseSchema = z.object({
  calls: z.array(GongCallListItemSchema),
});

/**
 * One party of an extensive call. Real Gong omits `emailAddress` on some
 * parties, omits `name` on some (Keystone prod: ~half of calls), and sends
 * `speakerId: null`.
 */
const GongCallPartySchema = z.object({
  speakerId: z.string().nullish(),
  name: z.string().nullish(),
  emailAddress: z.string().nullish(),
});

/** `POST /v2/calls/extensive`: each call is `{ metaData, parties }`. */
export const GongCallsExtensiveResponseSchema = z.object({
  calls: z.array(
    z.object({
      metaData: GongCallListItemSchema,
      parties: z.array(GongCallPartySchema).optional(),
    }),
  ),
});

/** `POST /v2/calls/transcript`. Real Gong sends `topic: null`. */
export const GongTranscriptResponseSchema = z.object({
  callTranscripts: z.array(
    z.object({
      callId: z.string(),
      transcript: z
        .array(
          z.object({
            speakerId: z.string(),
            topic: z.string().nullish(),
            sentences: z.array(
              z.object({
                start: z.number(),
                end: z.number(),
                text: z.string(),
              }),
            ),
          }),
        )
        .optional(),
    }),
  ),
});
